import { NavigableModal } from '$components'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { EditNotesForm, EditNotesSchema, Input } from '$features/pets/forms/edit-notes/index'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { petShowOptions, petShowQueryKey, petUpdateMutation } from '$api-client/@tanstack/react-query.gen'
import { ToastHelper } from '$components'
import { PetsFilterParam } from '$features/pets/enums/filter-params'

export function EditNotesModal() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()

  const { data } = useQuery({
    ...petShowOptions({
      path: {
        pet: Number(id),
      },
      query: {
        include_deleted: true,
        [PetsFilterParam.IncludeUnapproved]: searchParams.get(PetsFilterParam.IncludeUnapproved) === '1',
      },
    }),
  })

  const { mutate: updatePet, isPending: isUpdatingPet } = useMutation({
    ...petUpdateMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: petShowQueryKey({
          path: {
            pet: Number(id),
          },
        }),
      })

      ToastHelper.success('Pet notes updated successfully.')
      handleClose()
    },
    onError: () => ToastHelper.error('Failed to update pet notes.'),
  })

  const handleClose = () => navigate(-1)

  const handleSubmit = async (values: EditNotesSchema) => {
    updatePet({
      path: {
        pet: Number(id),
      },
      body: {
        grooming_preferences: values[Input.GroomingPreferences],
        internal_notes: values[Input.InternalNotes],
      },
    })
  }

  const pet = data?.data

  if (!pet) {
    return null
  }

  return (
    <EditNotesForm
      onSubmit={handleSubmit}
      onCancel={handleClose}
      isLoading={isUpdatingPet}
      defaultValues={{
        [Input.GroomingPreferences]: pet.grooming_preferences ?? undefined,
        [Input.InternalNotes]: pet.internal_notes ?? undefined,
      }}
    >
      <NavigableModal onClose={handleClose}>
        <NavigableModal.Header>
          <NavigableModal.Heading>
            Edit Notes
          </NavigableModal.Heading>
        </NavigableModal.Header>

        <NavigableModal.Content>
          <EditNotesForm.Content />
        </NavigableModal.Content>

        <NavigableModal.Footer>
          <EditNotesForm.Controls />
        </NavigableModal.Footer>
      </NavigableModal>
    </EditNotesForm>
  )
}
